// ═══════════════════════════════════════
// METER-PICKER — Dropdown sélecteur de mesure
// ═══════════════════════════════════════

import { updateHeaderLine } from './header-form.js';
import { highlight } from './editor.js';
import { render } from './renderer.js';

// ── État interne ──────────────────────────────────────────────────────────────

// Instance éditeur (hf-meter-trigger)
let _currentValue = '4/4';
let _currentType  = 'fraction';
let _fracNum      = 4;
let _fracDen      = '4';

// Instance modale (pp-meter-trigger) — stockée séparément
let _modal = null; // { value, type, fracNum, fracDen, onCommit }

const DENOMS = ['1', '2', '4', '8', '16', '32', '64', '128'];

function _typeFromValue(value) {
    if (value === 'C')  return 'C';
    if (value === 'C|') return 'C|';
    return 'fraction';
}

function _parseValue(value) {
    const type = _typeFromValue(value || '4/4');
    let fracNum = 4, fracDen = '4';
    if (type === 'fraction') {
        const parts = (value || '4/4').split('/');
        fracNum = parseInt(parts[0]) || 4;
        fracDen = parts[1] || '4';
    }
    return { type, fracNum, fracDen };
}

// ── Rendu du dropdown ─────────────────────────────────────────────────────────

function _renderFor(state, onCommit) {
    const dd = document.getElementById('meter-picker-dropdown');
    if (!dd) return;

    let html = `<div class="mp-list">`;

    const fracChecked = state.type === 'fraction' ? ' checked' : '';
    html += `
        <label class="mp-radio-row">
            <input type="radio" name="mp-type" value="fraction"${fracChecked}>
            <span class="mp-fraction-inline">
                <input type="number" id="mp-num" min="1" max="64" value="${state.fracNum}">
                <span class="mp-slash">/</span>
                <select id="mp-den">`;
    DENOMS.forEach(d => {
        html += `<option${d === state.fracDen ? ' selected' : ''}>${d}</option>`;
    });
    html += `        </select>
            </span>
        </label>`;

    [
        { type: 'C',  label: '<span class="mp-symbol">𝄴</span> (4/4)' },
        { type: 'C|', label: '<span class="mp-symbol">𝄵</span> (2/2)' },
    ].forEach(({ type, label }) => {
        const checked = state.type === type ? ' checked' : '';
        html += `
        <label class="mp-radio-row">
            <input type="radio" name="mp-type" value="${type}"${checked}>
            <span class="mp-radio-label">${label}</span>
        </label>`;
    });

    html += `</div>`;
    dd.innerHTML = html;

    dd.querySelectorAll('input[name="mp-type"]').forEach(radio => {
        radio.addEventListener('change', () => {
            state.type = radio.value;
            if (state.type === 'C' || state.type === 'C|') {
                onCommit(state.type, state);
                _close();
            } else {
                onCommit(`${state.fracNum}/${state.fracDen}`, state);
            }
        });
    });

    const numEl = document.getElementById('mp-num');
    const denEl = document.getElementById('mp-den');

    numEl.addEventListener('mousedown', () => { state.type = 'fraction'; });
    numEl.addEventListener('input', () => {
        state.type   = 'fraction';
        state.fracNum = parseInt(numEl.value) || 4;
        onCommit(`${state.fracNum}/${state.fracDen}`, state);
    });
    denEl.addEventListener('mousedown', () => { state.type = 'fraction'; });
    denEl.addEventListener('change', () => {
        state.type   = 'fraction';
        state.fracDen = denEl.value;
        onCommit(`${state.fracNum}/${state.fracDen}`, state);
    });
}

// ── Instances ─────────────────────────────────────────────────────────────────

let _activeTriggerId = null;

function _position(triggerId) {
    const trigger = document.getElementById(triggerId);
    const dd      = document.getElementById('meter-picker-dropdown');
    if (!trigger || !dd) return;

    const rect = trigger.getBoundingClientRect();
    dd.style.top  = (rect.bottom + window.scrollY + 4) + 'px';
    dd.style.left = (rect.left   + window.scrollX)     + 'px';

    requestAnimationFrame(() => {
        const r = dd.getBoundingClientRect();
        if (r.right > window.innerWidth - 8) {
            dd.style.left = (window.innerWidth - r.width - 8) + 'px';
        }
        if (r.bottom > window.innerHeight - 8) {
            dd.style.top = (trigger.getBoundingClientRect().top + window.scrollY - r.height - 4) + 'px';
        }
    });
}

function _close() {
    const dd = document.getElementById('meter-picker-dropdown');
    if (dd) dd.style.display = 'none';
    _activeTriggerId = null;
}

// ── Éditeur : commit dans l'ABC ───────────────────────────────────────────────

function _commitEditor(value, state) {
    _currentValue = value;
    _currentType  = state.type;
    _fracNum      = state.fracNum;
    _fracDen      = state.fracDen;
    _updateTriggerEditor();
    updateHeaderLine('M', value);
    highlight();
    render();
}

function _updateTriggerEditor() {
    const trigger = document.getElementById('hf-meter-trigger');
    if (trigger) trigger.textContent = _currentValue;
}

// ── API publique ──────────────────────────────────────────────────────────────

export function setMeterPickerValue(value) {
    const v = value || '4/4';
    _currentValue = v;
    const parsed  = _parseValue(v);
    _currentType  = parsed.type;
    _fracNum      = parsed.fracNum;
    _fracDen      = parsed.fracDen;
    _updateTriggerEditor();
}

export function initMeterPicker() {
    const triggerId = 'hf-meter-trigger';
    const trigger   = document.getElementById(triggerId);
    if (!trigger) return;

    trigger.addEventListener('click', e => {
        e.stopPropagation();
        const dd = document.getElementById('meter-picker-dropdown');
        if (dd && dd.style.display === 'block' && _activeTriggerId === triggerId) {
            _close();
        } else {
            const state = { type: _currentType, fracNum: _fracNum, fracDen: _fracDen };
            _activeTriggerId = triggerId;
            const dd2 = document.getElementById('meter-picker-dropdown');
            _renderFor(state, _commitEditor);
            _position(triggerId);
            dd2.style.display = 'block';
        }
    });

    document.addEventListener('mousedown', e => {
        const dd = document.getElementById('meter-picker-dropdown');
        if (!dd) return;
        const activeTrigger = _activeTriggerId ? document.getElementById(_activeTriggerId) : null;
        if (!dd.contains(e.target) && e.target !== activeTrigger) _close();
    });
}

/**
 * Init pour un trigger externe (modale).
 * onCommit(value) est appelé à chaque changement.
 */
export function initMeterPickerFor(triggerId, initialValue, onCommit) {
    const parsed = _parseValue(initialValue || '4/4');
    const state  = { ...parsed, value: initialValue || '4/4' };

    // Mettre à jour l'affichage du trigger
    const trigger = document.getElementById(triggerId);
    if (trigger) trigger.textContent = initialValue || '4/4';

    const fresh = trigger?.cloneNode(true);
    if (trigger && fresh) trigger.parentNode.replaceChild(fresh, trigger);

    fresh?.addEventListener('click', e => {
        e.stopPropagation();
        const dd = document.getElementById('meter-picker-dropdown');
        if (dd && dd.style.display === 'block' && _activeTriggerId === triggerId) {
            _close();
        } else {
            _activeTriggerId = triggerId;
            _renderFor(state, (value, s) => {
                state.value   = value;
                const trig    = document.getElementById(triggerId);
                if (trig) trig.textContent = value;
                onCommit(value);
            });
            _position(triggerId);
            const dd2 = document.getElementById('meter-picker-dropdown');
            if (dd2) dd2.style.display = 'block';
        }
    });
}
